/**
 * AR Technical Demo (F3 / GEN-2259) — register the Crestline vendor-portal
 * action tools (backup pull + claim filing) into the demo-script registry.
 *
 * THROWAWAY DEMO CODE. The portal beats (scenes 7-9 & 11) fire from whatever
 * page the presenter is on, so these tools are registered eagerly (registry-
 * only, like the voice-call + technical-checkpoint tools). Each makes sure the
 * deduction thread is on screen, then pops the portal as a MODAL OVERLAY on top
 * of it (`<CrestlinePortalOverlay>`, mounted in the authenticated layout) —
 * there is no separate portal route. They need a router `navigate` to focus the
 * thread, so the set is registered from this hook (called from `ArDemoControls`).
 *
 * Both action tools are AWAITABLE: the interceptor awaits each, so the scripted
 * narration only lands once the recorded agent session finishes playing
 * (`waitForPortalVideoEnd`, which also resolves on a missing/blocked video URL
 * so the demo never hangs). The HANDOFF onto the deduction thread is purely the
 * F0 checkpoint flip (`applyPortalBackupsFiled` / `applyClaimFiled`); the
 * overlay then closes and the thread (already on screen) shows the result.
 */

import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { registerArDemoTools } from './demo-script';
import { getArDemoState } from './fixture-store';
import { applyPortalBackupsFiled, applyClaimFiled } from './demo-checkpoints';
import { openPortal, closePortal, waitForPortalVideoEnd } from './portal-store';

const NO_ARGS_SCHEMA = z.toJSONSchema(z.object({})) as Record<string, unknown>;

/** Crestline portal confirmation # for the filed claim (matches the script). */
const CLAIM_CONFIRMATION = 'CR-CLAIM-44190';

type Navigate = ReturnType<typeof useNavigate>;

function tool(
  name: string,
  description: string,
  execute: () => Promise<unknown>,
): FrontendToolDefinition {
  return {
    name,
    description,
    awaitable: true,
    schema: { name, description, parameters: NO_ARGS_SCHEMA, awaitable: true },
    execute,
  };
}

/** Focus the deduction collection thread so the portal pops up over it. */
function goToDeductionThread(navigate: Navigate): Promise<unknown> {
  const { technical } = getArDemoState();
  return navigate({
    to: '/accounts-receivable/collections/$customerId/threads/$threadId',
    params: {
      customerId: technical.customerId,
      threadId: technical.deductionThreadId,
    },
    search: { from: 'threads' },
  });
}

/**
 * Pop the portal over the deduction thread and play the recorded session of
 * Nami signing in → finding #CR-DED-99214 → downloading the backup PDFs, then
 * (once the clip ends) file them onto the thread via the F0 checkpoint and
 * close the overlay.
 */
async function runBackupsPull(navigate: Navigate): Promise<void> {
  await goToDeductionThread(navigate);
  openPortal('backups');

  // The overlay plays the recorded agent session; wait for it to finish.
  await waitForPortalVideoEnd();

  // The portal artifacts flow into the AR system of record (F0 checkpoint
  // flips the shared fixture state — the thread's evidence section re-renders).
  applyPortalBackupsFiled();
  closePortal();

  // Surface the outcome: the portal backup is now filed onto the thread.
  toast.success('Backup filed to COL-1043', {
    description:
      'Pulled #CR-DED-99214’s backup from Crestline’s portal and filed it as evidence on the deduction thread.',
  });
}

/**
 * Pop the portal over the deduction thread and play the recorded session of
 * Nami opening the dispute form, filling it (deduction ref #CR-DED-99214,
 * $3,100, supporting docs) and submitting, then (once the clip ends) stamp the
 * filed claim via the F0 checkpoint, close the overlay, and surface the
 * confirmation #.
 */
async function runClaimFiling(navigate: Navigate): Promise<void> {
  await goToDeductionThread(navigate);
  openPortal('claim');

  // The overlay plays the recorded agent session; wait for it to finish.
  await waitForPortalVideoEnd();

  applyClaimFiled();
  closePortal();

  // Surface the outcome + the scheduled next-day re-check.
  const tomorrow = new Date(Date.now() + 86_400_000).toLocaleDateString(
    'en-US',
    { month: 'short', day: 'numeric' },
  );
  toast.success(`Dispute claim filed — #${CLAIM_CONFIRMATION}`, {
    description: `$3,100 chargeback #CR-DED-99214 disputed on Crestline’s portal. Nami will re-check for a response on ${tomorrow}.`,
  });
}

/**
 * Register the portal tools. Re-registers when the router `navigate` identity
 * changes (mirrors `useTechnicalDemoTools`) so the captured navigate never goes
 * stale. The portal renders as an overlay (`CrestlinePortalOverlay`) driven by
 * `portal-store`, so no page needs to mount to make these resolve.
 */
export function usePortalDemoTools(): void {
  const navigate = useNavigate();

  const tools = useMemo<FrontendToolDefinition[]>(
    () => [
      tool(
        'ar_tech_pull_portal_backups',
        "Operate Crestline's vendor portal to download chargeback #CR-DED-99214's backup PDFs and file them onto deduction thread COL-1043.",
        () => runBackupsPull(navigate),
      ),
      tool(
        'ar_tech_file_portal_claim',
        "File a dispute claim on Crestline's vendor portal for the $3,100 unsupported chargeback #CR-DED-99214 and return the confirmation #.",
        () => runClaimFiling(navigate),
      ),
      tool(
        'ar_tech_navigate_portal',
        "Open Crestline's vendor portal (mock) over the deduction thread to pull deduction backups or file a dispute claim.",
        async () => {
          await goToDeductionThread(navigate);
          openPortal('backups');
        },
      ),
    ],
    [navigate],
  );

  useMemo(() => {
    registerArDemoTools('ar-tech-portal', tools);
  }, [tools]);
}
